import React, { useState, useEffect, useCallback } from 'react';
import {
  Tabs,
  Button,
  List,
  Avatar,
  Tag,
  Dropdown,
  Modal,
  message,
  Col,
  Row,
  Spin,
  Alert,
  Typography,
  Space,
  Empty,
} from 'antd';
import { DownOutlined, EditOutlined, UserDeleteOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import type { MenuProps } from 'antd';
import { Estate, EstateRole, ParticipantResponse, User } from '../../types';
import { deleteParticipant } from './participantService';
import { addExecutorToParticipants, addLawyerToParticipants, fetchEstateDetails, removeFromEstateRole } from '../estatelist/estateService';
import { useGlobalContext } from '../../store/GlobalContext';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import AuditTrailToggle from '../audittrail/AuditTrailToggle';
import AddParticipantModal from './addparticipant';
import InviteRoleModal from '../estateroles/inviterolemodal';
import { beneficiaryLevelColor, beneficiaryLevelLabel } from './beneficiaryLevels';
import EstateSelectorModal from '../estates/estateselector';
import { formatEmail, isSyntheticEmail } from '../estatelist/estatedetails';

const { TabPane } = Tabs;
const { Title, Text } = Typography;

const getInitials = (firstName?: string | null, lastName?: string | null, email?: string | null) => {
  if (firstName && lastName) return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  if (lastName) return lastName.slice(0, 2).toUpperCase();
  if (email && !isSyntheticEmail(email)) return email.slice(0, 2).toUpperCase();
  return 'U';
};

const fullName = (person?: { prefix?: string | null; firstName?: string | null; middleName?: string | null; lastName?: string | null; suffix?: string | null } | null) =>
  [person?.prefix, person?.firstName, person?.middleName, person?.lastName, person?.suffix]
    .filter(Boolean)
    .join(' ');

// Styled Components
const ParticipantsContainer = styled.div`
  padding: 1rem;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const ParticipantItem = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 8px;
  background-color: #f4f6fb;
  margin-bottom: 0.5rem;
  border: 1px solid #e0e0e0;
`;

const ParticipantDetails = styled.div`
  flex: 1 1 200px;
  min-width: 0;
`;

const ParticipantActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-left: auto;
`;

const ParticipantHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ParticipantsPage: React.FC = () => {
  const [participants, setParticipants] = useState<ParticipantResponse[]>([]);
  const [executors, setExecutors] = useState<User[]>([]);
  const [lawyers, setLawyers] = useState<User[]>([]);
  const [invites, setInvites] = useState<User[]>([]);
  const [invitedGuests, setInvitedGuests] = useState<any[]>([]);
  const [estateDetails, setEstateDetails] = useState<Estate | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<string>('Admin');

  const [isAddParticipantModalVisible, setIsAddParticipantModalVisible] = useState<boolean>(false);
  const [isAddExecutorModalVisible, setIsAddExecutorModalVisible] = useState<boolean>(false);
  const [isAddLawyerModalVisible, setIsAddLawyerModalVisible] = useState<boolean>(false);
  const [participantToEdit, setParticipantToEdit] = useState<ParticipantResponse | null>(null);
  const [isEstateSelectorVisible, setIsEstateSelectorVisible] = useState<boolean>(false);

  const { selectedEstate } = useGlobalContext();
  const estateId = selectedEstate?.uuid || '';

  const navigate = useNavigate();

  /**
   * The estate endpoint returns every roster (participants, executors, lawyers,
   * invites and guests) in one response, so the page reads from it rather than
   * fanning out to a request per tab.
   */
  const loadEstateRosters = useCallback(async () => {
    if (!estateId) return;

    setLoading(true);
    setError(null);
    try {
      const token = Cookies.get('accessToken') || Cookies.get('token');
      if (!token) {
        setError('Unauthorized. Please log in.');
        return;
      }

      const estateData = await fetchEstateDetails(estateId, token);
      setEstateDetails(estateData);
      setParticipants((estateData.participants || []) as ParticipantResponse[]);
      setExecutors(estateData.executor || []);
      setLawyers(estateData.lawyers || []);
      setInvites(estateData.invites || []);
      setInvitedGuests(estateData.guests || []);
    } catch (err: any) {
      console.error(err);
      if (err.message === 'Unauthorized') {
        message.error('Unauthorized. Please log in.');
        navigate('/login');
        return;
      }
      setError(err.message || 'Failed to load participants.');
    } finally {
      setLoading(false);
    }
  }, [estateId, navigate]);

  useEffect(() => {
    loadEstateRosters();
  }, [loadEstateRosters]);

  /**
   * Participants in the order they would take, so the roster reads as the line
   * of succession rather than as the order people happened to be entered.
   * Anyone without a level sits at the end: no answer is not the same as
   * inheriting last, so they are grouped apart rather than sorted among the
   * levels.
   */
  /**
   * Whether somebody is already on the participants roster.
   *
   * An executor or an attorney need not be one: the rosters answer different
   * questions, and the estate's own lawyer inherits nothing. So the offer to
   * add them stands only while they are absent, rather than being shown to
   * somebody who is already there and would be refused.
   */
  const isParticipant = React.useCallback(
    (uid?: string) => !!uid && participants.some((p) => p.uid === uid),
    [participants],
  );

  const participantsByLevel = React.useMemo(
    () =>
      [...participants].sort((a, b) => {
        const left = a.beneficiaryLevel ?? Number.POSITIVE_INFINITY;
        const right = b.beneficiaryLevel ?? Number.POSITIVE_INFINITY;
        return left - right;
      }),
    [participants],
  );

  // Role Menu items and handler
  const items: MenuProps['items'] = [
    { label: 'User', key: 'User' },
    { label: 'Guest', key: 'Guest' },
    { label: 'Admin', key: 'Admin', danger: true, disabled: true },
    { label: 'Executor', key: 'Executor', danger: true },
  ];

  const handleRoleChange = (newRole: string) => {
    Modal.confirm({
      title: 'Confirm Role Change',
      content: `Are you sure you want to change the role to ${newRole}?`,
      onOk: () => setRole(newRole),
      okText: 'Yes',
      cancelText: 'No',
    });
  };

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    message.info(`Role changed to ${e.key}`);
    handleRoleChange(e.key);
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  const RoleMenu = (
    <Dropdown menu={menuProps}>
      <Button>
        <Space>
          Change Role
          <DownOutlined />
        </Space>
      </Button>
    </Dropdown>
  );

  // Handle Adding a Participant. Clearing the edit target is what puts the
  // shared modal into create mode, so a prior edit cannot leak into the form.
  const handleAddParticipant = () => {
    setParticipantToEdit(null);
    setIsAddParticipantModalVisible(true);
  };

  // Handle Editing a Participant
  const handleEditParticipant = (participant: ParticipantResponse) => {
    setParticipantToEdit(participant);
    setIsAddParticipantModalVisible(true);
  };

  // Handle Success of an Executor or Lawyer Invitation. The invite only creates
  // a pending record, so re-read the rosters to pick it up. The modal reports
  // the result itself, so this stays quiet.
  const handleExecutorInviteSuccess = () => {
    setIsAddExecutorModalVisible(false);
    loadEstateRosters();
  };

  const handleLawyerInviteSuccess = () => {
    setIsAddLawyerModalVisible(false);
    loadEstateRosters();
  };

  // Handle Deleting a Participant
  const handleDeleteParticipant = (participantId: string) => {
    Modal.confirm({
      title: 'Confirm Deletion',
      content: 'Are you sure you want to delete this participant?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const token = Cookies.get('token') || Cookies.get('accessToken');
          if (!token) {
            message.error('Unauthorized. Please log in.');
            return;
          }

          await deleteParticipant(estateId, participantId, token);
          setParticipants((prev) => prev.filter((p) => p.uid !== participantId));
          setExecutors((prev) => prev.filter((e) => e.uid !== participantId));
          setLawyers((prev) => prev.filter((l) => l.uid !== participantId));
          setInvites((prev) => prev.filter((i) => i.uid !== participantId));
          setInvitedGuests((prev) => prev.filter((g) => g.uid !== participantId));
          message.success('Participant deleted successfully');
        } catch (err: any) {
          console.error(err);
          message.error(err.message || 'Failed to delete participant');
        }
      },
    });
  };

  /**
   * Take somebody off the executor or lawyer roster.
   *
   * Not the same act as deleting a participant, which is why it is a separate
   * handler: this ends the role they act in and leaves them a participant if
   * they are also one. Deleting them as a participant would refuse outright for
   * anyone who only ever acted for the estate.
   */
  const handleRemoveFromRole = (role: EstateRole, personUid: string) => {
    const label = role === 'lawyer' ? 'lawyer' : 'executor';
    Modal.confirm({
      title: `Remove ${label}`,
      content: `Remove this person as ${label === 'executor' ? 'an' : 'a'} ${label} of the estate? They stay a participant if they are one.`,
      okText: 'Remove',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await removeFromEstateRole(estateId, role, personUid);
          await loadEstateRosters();
          message.success(`Removed as ${label} successfully`);
        } catch (err: any) {
          console.error(err);
          message.error(err.message || `Failed to remove ${label}`);
        }
      },
    });
  };

  const handleAddExecutorToParticipant = async (executorUid: string) => {
    try {
      const token = Cookies.get('token') || Cookies.get('accessToken');
      if (!token || !estateId) {
        message.error('Unauthorized. Please log in.');
        return;
      }

      await addExecutorToParticipants(estateId, executorUid);
      await loadEstateRosters();
      message.success('Executor added to participants successfully');
    } catch (err: any) {
      console.error(err);
      message.error(err.message || 'Failed to add executor to participants');
    }
  };

  const handleAddLawyerToParticipant = async (lawyerUid: string) => {
    try {
      const token = Cookies.get('token') || Cookies.get('accessToken');
      if (!token || !estateId) {
        message.error('Unauthorized. Please log in.');
        return;
      }

      await addLawyerToParticipants(estateId, lawyerUid);
      await loadEstateRosters();
      message.success('Lawyer added to participants successfully');
    } catch (err: any) {
      console.error(err);
      message.error(err.message || 'Failed to add lawyer to participants');
    }
  };

  /*
   * An edit can move someone between rosters (an invite becomes a participant,
   * say), so the lists are re-read rather than patched in place. The modal
   * announces the save itself, so this only refreshes.
   */
  const handleParticipantSuccess = async () => {
    setParticipantToEdit(null);
    setIsAddParticipantModalVisible(false);
    await loadEstateRosters();
  };

  const renderPersonRow = (
    person: ParticipantResponse | User | any,
    options: {
      tags?: React.ReactNode;
      extraActions?: React.ReactNode;
      emptyName: string;
      /** What the delete button removes. Defaults to the participant record. */
      onDelete?: (uid: string) => void;
    }
  ) => (
    <ParticipantItem key={person.uid || person.id}>
      <Avatar src={person.profilePic}>
        {!person.profilePic && getInitials(person.firstName, person.lastName, person.email)}
      </Avatar>
      <ParticipantDetails>
        <ParticipantHeader>
          <Text strong>{fullName(person) || options.emptyName}</Text>
        </ParticipantHeader>
        <Text type="secondary">{formatEmail(person.email)} </Text>
        {person.pendingEmail && <Tag color="gold">Pending: {person.pendingEmail}</Tag>}
        {options.tags}
      </ParticipantDetails>
      <ParticipantActions>
        {options.extraActions}
        {RoleMenu}
        <Button
          type="text"
          aria-label={`Edit ${fullName(person) || options.emptyName}`} icon={<EditOutlined />}
          onClick={() => handleEditParticipant(person as ParticipantResponse)}
        />
        <Button
          type="text"
          aria-label={`Remove ${fullName(person) || options.emptyName}`} icon={<UserDeleteOutlined />}
          onClick={() => (options.onDelete ?? handleDeleteParticipant)(person.uid)}
        />
      </ParticipantActions>
    </ParticipantItem>
  );

  // No active estate: participants are estate-scoped, so ask for one rather
  // than firing a request with an empty estate id and surfacing its error.
  if (!selectedEstate) {
    return (
      <Row gutter={[16, 16]} style={{ marginTop: '24px', marginLeft: 0, marginRight: 0 }}>
        <Col xs={24}>
          <ParticipantsContainer>
            <Title level={4}>Participants</Title>
            <Empty
              description="Select an estate to see its participants, executors, invites and guests."
              style={{ padding: '2rem 0' }}
            >
              <Button type="primary" onClick={() => setIsEstateSelectorVisible(true)}>
                Select Estate
              </Button>
            </Empty>
          </ParticipantsContainer>
          <EstateSelectorModal
            visible={isEstateSelectorVisible}
            onClose={() => setIsEstateSelectorVisible(false)}
          />
        </Col>
        <AuditTrailToggle />
      </Row>
    );
  }

  return (
    <Row gutter={[16, 16]} style={{ marginTop: '24px', marginLeft: 0, marginRight: 0 }}>
      {/* Participants and Actions */}
      <Col xs={24}>
        <ParticipantsContainer>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
            <Space direction="vertical" size={2}>
              <Title level={1} style={{ margin: 0, fontSize: 28 }}>Participants</Title>
              <Text type="secondary">{selectedEstate.estateName}</Text>
            </Space>
            <Space wrap>
              <Button type="primary" onClick={handleAddParticipant}>
                Add Participant
              </Button>
              <Button onClick={() => setIsAddExecutorModalVisible(true)}>
                Add Executor
              </Button>
              <Button onClick={() => setIsAddLawyerModalVisible(true)}>
                Add Lawyer
              </Button>
              <Button onClick={() => navigate(`/estate/${estateId}`)}>View Estate</Button>
            </Space>
          </div>

          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              action={
                <Button size="small" type="primary" onClick={loadEstateRosters}>
                  Retry
                </Button>
              }
              style={{ marginBottom: '16px' }}
            />
          )}

          {!error && <Spin spinning={loading}>
            <Tabs defaultActiveKey="1">
              {/* Participants Tab */}
              <TabPane tab="Participants" key="1">
                <List
                  dataSource={participantsByLevel}
                  renderItem={(participant) =>
                    renderPersonRow(participant, {
                      emptyName: 'Unnamed Participant',
                      tags: (
                        <>
                          {participant.beneficiaryLevel ? (
                            <Tag color={beneficiaryLevelColor(participant.beneficiaryLevel)}>
                              {beneficiaryLevelLabel(participant.beneficiaryLevel)}
                            </Tag>
                          ) : null}
                          {participant.addresses?.[0]?.city ? (
                            <Tag color="blue">{participant.addresses[0].city}</Tag>
                          ) : null}
                        </>
                      ),
                    })
                  }
                />
              </TabPane>

              {/* Executors Tab */}
              <TabPane tab="Executors" key="2">
                <List
                  dataSource={executors}
                  renderItem={(executor) =>
                    renderPersonRow(executor, {
                      emptyName: 'Unnamed Executor',
                      tags: <Tag color="green">{executor.isAdmin ? 'Admin' : 'Executor'}</Tag>,
                      onDelete: (uid: string) => handleRemoveFromRole('executor', uid),
                      extraActions: isParticipant(executor.uid) ? (
                        <Tag color="blue">Also a participant</Tag>
                      ) : (
                        <Button type="primary" onClick={() => handleAddExecutorToParticipant(executor.uid)}>
                          Add as Participant
                        </Button>
                      ),
                    })
                  }
                />
              </TabPane>

              {/* Lawyers Tab */}
              <TabPane tab="Lawyers" key="5">
                <List
                  dataSource={lawyers}
                  locale={{ emptyText: 'No lawyer is acting for this estate yet.' }}
                  renderItem={(lawyer) =>
                    renderPersonRow(lawyer, {
                      emptyName: 'Unnamed Lawyer',
                      tags: <Tag color="volcano">Lawyer</Tag>,
                      onDelete: (uid: string) => handleRemoveFromRole('lawyer', uid),
                      extraActions: isParticipant(lawyer.uid) ? (
                        <Tag color="blue">Also a participant</Tag>
                      ) : (
                        <Button type="primary" onClick={() => handleAddLawyerToParticipant(lawyer.uid)}>
                          Add as Participant
                        </Button>
                      ),
                    })
                  }
                />
              </TabPane>

              {/* Invites Tab */}
              <TabPane tab="Invites" key="3">
                <List
                  dataSource={invites}
                  renderItem={(invite) =>
                    renderPersonRow(invite, {
                      emptyName: 'Unnamed Invite',
                      tags: <Tag color="purple">Invites</Tag>,
                    })
                  }
                />
              </TabPane>

              {/* Invited Guests Tab */}
              <TabPane tab="Guests" key="4">
                <List
                  dataSource={invitedGuests}
                  renderItem={(guest) =>
                    renderPersonRow(guest, {
                      emptyName: 'Unnamed Guest',
                      tags: <Tag color="purple">Guest</Tag>,
                    })
                  }
                />
              </TabPane>
            </Tabs>
          </Spin>}
        </ParticipantsContainer>

        {/* Add or Edit Participant Modal */}
        <AddParticipantModal
          visible={isAddParticipantModalVisible}
          onCancel={() => {
            setIsAddParticipantModalVisible(false);
            setParticipantToEdit(null);
          }}
          onSuccess={handleParticipantSuccess}
          estateId={estateId}
          estate={estateDetails}
          initialValues={participantToEdit || undefined}
          isEdit={!!participantToEdit}
        />

        {/* Add Executor Modal */}
        <InviteRoleModal
          open={isAddExecutorModalVisible}
          onCancel={() => setIsAddExecutorModalVisible(false)}
          onSuccess={handleExecutorInviteSuccess}
          estateId={estateId}
          role="executor"
        />

        {/* Add Lawyer Modal */}
        <InviteRoleModal
          open={isAddLawyerModalVisible}
          onCancel={() => setIsAddLawyerModalVisible(false)}
          onSuccess={handleLawyerInviteSuccess}
          estateId={estateId}
          role="lawyer"
        />
      </Col>

      <AuditTrailToggle />
    </Row>
  );
};

export default ParticipantsPage;
